import React from 'react';
import { X } from 'lucide-react';
import type { AvailableQuestion } from '@/services/PagesServices/AssessmentManagementService';

interface AssessmentPreviewModalProps {
  title: string;
  description: string;
  assessmentType: string;
  timeLimit: string;
  passingScore: string | null;
  attemptsAllowed: string | null;
  randomizeQuestions: boolean;
  immediateFeedback: boolean;
  questions: AvailableQuestion[];
  totalPoints: number;
  onClose: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '40px 16px',
  overflowY: 'auto',
  zIndex: 1000,
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  width: '100%',
  maxWidth: 760,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  overflow: 'hidden',
};

const header: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid #e5e7eb',
  background: '#f8fafc',
};

const body: React.CSSProperties = {
  padding: '20px 24px',
};

const metaRow: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px 16px',
  padding: '12px 16px',
  background: '#f1f5f9',
  borderRadius: 8,
  margin: '12px 0 20px',
  fontSize: 13,
  color: '#475569',
};

const metaItem: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const questionCard: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '14px 16px',
  marginBottom: 12,
};

const questionHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
  fontSize: 12,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const optionRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 0',
  fontSize: 14,
};

const correctBadge: React.CSSProperties = {
  marginLeft: 'auto',
  fontSize: 11,
  fontWeight: 600,
  color: '#15803d',
  background: '#dcfce7',
  padding: '2px 6px',
  borderRadius: 999,
};

const closeBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#475569',
  padding: 4,
  display: 'inline-flex',
};

const footer: React.CSSProperties = {
  padding: '12px 20px',
  borderTop: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'flex-end',
  background: '#f8fafc',
};

const closeButton: React.CSSProperties = {
  background: '#1f2937',
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
};

const AssessmentPreviewModal: React.FC<AssessmentPreviewModalProps> = ({
  title,
  description,
  assessmentType,
  timeLimit,
  passingScore,
  attemptsAllowed,
  randomizeQuestions,
  immediateFeedback,
  questions,
  totalPoints,
  onClose,
}) => {
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div style={card} onClick={stopPropagation}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Preview
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>
              {title || 'Untitled assessment'}
            </div>
          </div>
          <button type="button" style={closeBtn} onClick={onClose} aria-label="Close preview">
            <X size={20} />
          </button>
        </div>

        <div style={body}>
          {description ? (
            <p style={{ color: '#334155', marginTop: 0 }}>{description}</p>
          ) : (
            <p style={{ color: '#94a3b8', marginTop: 0, fontStyle: 'italic' }}>
              No description provided.
            </p>
          )}

          <div style={metaRow}>
            <span style={metaItem}>
              <strong style={{ color: '#0f172a' }}>Type:</strong>{' '}
              {assessmentType ? assessmentType : '—'}
            </span>
            <span style={metaItem}>
              <strong style={{ color: '#0f172a' }}>Time:</strong>{' '}
              {timeLimit ? `${timeLimit} min` : '—'}
            </span>
            <span style={metaItem}>
              <strong style={{ color: '#0f172a' }}>Passing:</strong>{' '}
              {passingScore == null ? 'Not required' : `${passingScore}%`}
            </span>
            <span style={metaItem}>
              <strong style={{ color: '#0f172a' }}>Attempts:</strong>{' '}
              {attemptsAllowed == null ? 'Unlimited' : attemptsAllowed}
            </span>
            <span style={metaItem}>
              <strong style={{ color: '#0f172a' }}>Questions:</strong>{' '}
              {questions.length} ({totalPoints} pts)
            </span>
            <span style={metaItem}>
              <strong style={{ color: '#0f172a' }}>Randomize:</strong>{' '}
              {randomizeQuestions ? 'Yes' : 'No'}
            </span>
            <span style={metaItem}>
              <strong style={{ color: '#0f172a' }}>Immediate feedback:</strong>{' '}
              {immediateFeedback ? 'Yes' : 'No'}
            </span>
          </div>

          {questions.length === 0 ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: '#94a3b8',
                border: '1px dashed #cbd5e1',
                borderRadius: 8,
              }}
            >
              No questions selected yet.
            </div>
          ) : (
            questions.map((q, idx) => {
              const isChoice = q.type === 'MCQ' || q.type === 'T/F';
              const opts =
                q.options && q.options.length > 0
                  ? q.options
                  : q.type === 'T/F'
                  ? ['True', 'False']
                  : [];
              return (
                <div key={q.id} style={questionCard}>
                  <div style={questionHeader}>
                    <span>
                      Q{idx + 1} · {q.type} · {q.difficulty}
                    </span>
                    <span>{q.points} pts</span>
                  </div>
                  <div style={{ fontSize: 15, color: '#0f172a', marginBottom: 10 }}>
                    {q.question}
                  </div>

                  {isChoice && opts.length > 0 && (
                    <div>
                      {opts.map((opt, i) => {
                        const isCorrect = q.correctAnswer === opt;
                        return (
                          <label
                            key={i}
                            style={{
                              ...optionRow,
                              background: isCorrect ? '#f0fdf4' : 'transparent',
                              borderRadius: 6,
                              padding: isCorrect ? '6px 8px' : '6px 0',
                            }}
                          >
                            <input type="radio" disabled name={`preview-q-${q.id}`} />
                            <span>{opt}</span>
                            {isCorrect && <span style={correctBadge}>Correct</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {q.type === 'Short' && (
                    <input
                      type="text"
                      disabled
                      placeholder="Learner's short answer"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        background: '#f8fafc',
                      }}
                    />
                  )}

                  {q.type === 'Essay' && (
                    <textarea
                      disabled
                      rows={3}
                      placeholder="Learner's essay response"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        background: '#f8fafc',
                        resize: 'vertical',
                      }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        <div style={footer}>
          <button type="button" style={closeButton} onClick={onClose}>
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPreviewModal;
